<script setup lang="ts">
import { onUnmounted, ref } from "vue";
import {
  __color,
  Color,
  useClipboard,
  useCompatableDownMovePoint$
} from "@zyzgroup/core-web";
import { clamp } from "@zyzgroup/core-common";
import Popover from "../pop/Popover.vue";
import Box from "../base/Box.vue";
// import { toast } from "../../globalTeleport";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Popover }
});

const props = withDefaults(
  defineProps<{
    color?: string;
  }>(),
  {
    color: "#5a82f0"
  }
);

const emits = defineEmits<{
  (event: "color", color: string): void;
}>();

const popover = ref();

const color = new Color(props.color);
const rightColor = new Color(props.color);

const colorString = ref("");
const oppositeColorString = ref("");
const rightColorString = ref("");
const oppositeRightColorString = ref("");

const h = ref(0);
const s = ref(0);
const v = ref(0);

const hTop = ref(0);
const sLeft = ref(0);
const vTop = ref(0);

function set() {
  colorString.value = color.toString();
  oppositeColorString.value = __color.set(color).opposite(true).toString();
  rightColorString.value = rightColor.toString();
  oppositeRightColorString.value = __color
    .set(rightColor)
    .opposite(true)
    .toString();
  const rightColorHSV = rightColor.getHSV();
  const colorHSV = color.getHSV();
  h.value = rightColorHSV.h;
  s.value = colorHSV.s;
  v.value = colorHSV.v;
  hTop.value = (1 - h.value) * 100;
  sLeft.value = 100 * s.value - 6;
  vTop.value = 100 * (1 - v.value) - 6;
}

// 初始化
set();

let s1;
let s2;

// 右侧竖行选择区-指示棒-h
const hueFieldReady = (ctx, box) => {
  const setH = () => {
    if (!ctx.mpg.current.isDown) {
      return;
    }
    const h = clamp(1 - ctx.mpg.current.y / ctx.rect.height, 0, 1);
    rightColor.setHSV(h, s.value, v.value);
    color.setHSV(h, s.value, v.value);
    set();
  };
  if (s1) {
    s1.unsubscribe();
  }
  s1 = useCompatableDownMovePoint$(box.el, {
    start: setH
  }).subscribe(setH);
};

// 正方形选择区-叠加黑白渐变背景-s+v
const saturationFieldReady = (ctx, box) => {
  const setSV = () => {
    if (!ctx.mpg.current.isDown) {
      return;
    }
    color.setHSV(
      h.value,
      ctx.mpg.current.x / ctx.rect.width,
      1 - ctx.mpg.current.y / ctx.rect.height
    );
    set();
  };
  if (s2) {
    s2.unsubscribe();
  }
  s2 = useCompatableDownMovePoint$(box.el, {
    start: setSV,
    end: () => {
      emits("color", colorString.value);
      popover.value.close();
      useClipboard(colorString.value).then(() => {
        // toast(`${colorString.value} 已复制`);
      });
    }
  }).subscribe(setSV);
};

onUnmounted(() => {
  s1?.unsubscribe();
  s2?.unsubscribe();
});

defineExpose({});
</script>

<template>
  <Popover
    ref="popover"
    :closeWhenClickOverlay="false"
  >
    <template #button>
      <slot>
        <span
          style="
            display: inline-block;
            vertical-align: middle;
            font-size: 1em;
            padding: 0.3em 0.8em;
            border-radius: 0.3em;
            cursor: pointer;
            outline: 'none';
            text-align: 'center';
            border: 0;
          "
          :style="{
            backgroundColor: colorString,
            color: oppositeColorString
          }"
        >
          {{ colorString }}
        </span>
      </slot>
    </template>
    <template #panel>
      <div
        class="selector"
        style="
          position: relative;
          width: 124px;
          height: 106px;
          padding: 3px;
          background-color: #222222;
          box-sizing: border-box;
        "
      >
        <!-- 正方形选择区 -->
        <div
          class="saturation-field"
          style="
            position: relative;
            width: 100px;
            height: 100px;
            display: inline-block;
            cursor: pointer;
            box-sizing: border-box;
          "
          :style="{
            background: `linear-gradient(
              to right,
              rgb(255, 255, 255) 0%,
              ${rightColorString} 100%
            )`
          }"
        >
          <Box
            display="full"
            @start="saturationFieldReady"
          >
            <!-- 正方形选择区-叠加黑白渐变背景 -->
            <div
              style="
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  to bottom,
                  rgba(0, 0, 0, 0) 0%,
                  rgb(0, 0, 0) 100%
                );
                box-sizing: border-box;
              "
            ></div>
            <!-- 圆形选择点 -->
            <div
              class="field-knob"
              style="
                position: absolute;
                width: 12px;
                height: 12px;
                box-shadow: rgba(0, 0, 0, 0.5) 0px 1px 3px;
                border-radius: 12px;
                z-index: 1;
                cursor: pointer;
                box-sizing: border-box;
              "
              :style="{
                left: sLeft + 'px',
                top: vTop + 'px',
                backgroundColor: colorString,
                border: `2px solid ${oppositeColorString}`
              }"
            ></div>
          </Box>
        </div>
        <!-- 右侧竖行选择区 -->
        <div
          class="hue-field"
          style="
            width: 15px;
            height: 100px;
            cursor: ns-resize;
            position: absolute;
            top: 3px;
            right: 3px;
            background: linear-gradient(
              to bottom,
              rgb(255, 0, 0) 0%,
              rgb(255, 0, 255) 17%,
              rgb(0, 0, 255) 34%,
              rgb(0, 255, 255) 50%,
              rgb(0, 255, 0) 67%,
              rgb(255, 255, 0) 84%,
              rgb(255, 0, 0) 100%
            );
            box-sizing: border-box;
          "
        >
          <Box
            display="full"
            @start="hueFieldReady"
          >
            <!-- 右侧竖行选择区-指示棒 -->
            <div
              class="hue-knob"
              style="
                position: absolute;
                width: 15px;
                height: 2px;
                z-index: 1;
                box-sizing: border-box;
              "
              :style="{
                marginTop: hTop + 'px',
                borderRight: '4px solid ' + oppositeRightColorString
              }"
            ></div>
          </Box>
        </div>
      </div>
    </template>
  </Popover>
</template>
